import React from 'react'
import { Modal, Form, Input, Select, Switch, DatePicker, Checkbox, Tag } from 'antd'
import { FormComponentProps } from 'antd/lib/form'
import moment from 'moment'
import { COMMISSION_SELECT_STYLES } from '@pages/Clients/Agent/Commissions/CreateNewCommission/commissionSelectStyles'
import { BENEFIT_TYPES, ACTION_TYPES } from './benefitConstants'
import { SectionTitle, StepBar, renderWizardFooter, makeWizardHandlers, SendNotificationCheckbox } from '../shared/mediwhoAppStyles'
import { parseTargets, makeTargetTypeHandler, TargetChipField } from '../shared/ChipInput'

type TargetType = 'all' | 'ids' | 'phones' | 'emails'

const { CheckableTag } = Tag

const TARGET_OPTIONS: Array<{ label: string; value: TargetType }> = [
    { label: 'מזהי משתמש', value: 'ids' },
    { label: 'טלפונים', value: 'phones' },
    { label: 'אימיילים', value: 'emails' }
]

const STEPS = [
    { label: 'תוכן', desc: 'כותרות ותיאורים' },
    { label: 'הגדרות', desc: 'סוג ותנאים' },
    { label: 'פרסום', desc: 'קהל יעד' }
]

const STEP_FIELDS = [
    ['short_title', 'title', 'sub_title', 'short_description', 'description'],
    ['benefit_type', 'action_type', 'terms'],
    [] as string[]
]

const REQUIRED_CREATE_FIELDS = ['short_title', 'title', 'terms', 'benefit_type', 'action_type']

interface Props extends FormComponentProps {
    visible: boolean
    benefit: any
    activeCount: number
    inactiveCount: number
    isPartner?: boolean
    takenByOthers?: number[]
    onClose: () => void
    // Modal always emits body — page owns create/update
    onSaved: (
        triggerNotification: boolean,
        benefitData: { title: string; description: string },
        body: any
    ) => Promise<boolean>
}

interface State {
    saving: boolean
    step: number
    hasWhereToUse: boolean
    hasTermsLink: boolean
    hasActionValue: boolean
    hasExpiration: boolean
    hasSortOrder: boolean
    targetType: TargetType
    isOther: boolean
    savedTargets: Partial<Record<TargetType, string>>
    targetsInitial: string
    sendNotification: boolean
    savedValues: Record<string, any>
    isActive: boolean
}

class BenefitModal extends React.Component<Props, State> {
    private saveLock = false

    state: State = {
        saving: false,
        step: 0,
        hasWhereToUse: false,
        hasTermsLink: false,
        hasActionValue: !!this.props.isPartner,
        hasExpiration: false,
        hasSortOrder: false,
        targetType: 'phones' as TargetType,
        isOther: false,
        savedTargets: {},
        targetsInitial: '',
        sendNotification: false,
        savedValues: {},
        isActive: true
    }

    // Shared wizard helpers — no more copy-paste across modals
    get wizard() {
        return makeWizardHandlers(
            this.props.form,
            STEP_FIELDS,
            () => this.state,
            updater => this.setState(updater)
        )
    }

    componentDidUpdate(prev: Props) {
        if (this.props.benefit !== prev.benefit || this.props.visible !== prev.visible) {
            const b = this.props.benefit
            const targetType: TargetType = (b && b.target_type && b.target_type !== 'all'
                ? b.target_type
                : 'phones') as TargetType
            const isOther = !!(b && b.target_type && b.target_type !== 'all')
            let targetsInitial = ''
            if (b && b.target_value) {
                try {
                    const arr = Array.isArray(b.target_value) ? b.target_value : JSON.parse(b.target_value)
                    targetsInitial = arr.join('\n')
                } catch (_) {
                    targetsInitial = parseTargets(String(b.target_value)).join('\n')
                }
            }
            this.setState({
                step: 0,
                hasWhereToUse: !!(b && b.where_to_use),
                hasTermsLink: !!(b && b.terms_link),
                hasActionValue: this.props.isPartner || !!(b && b.action_value),
                hasExpiration: !!(b && b.expiration_date),
                hasSortOrder: !!(b && b.sort_order),
                isActive: b ? !!b.is_active : true,
                targetType,
                isOther,
                targetsInitial,
                savedTargets: targetsInitial ? { [targetType]: targetsInitial } : {},
                sendNotification: false,
                savedValues: {}
            })
            this.props.form.resetFields()
        }
    }

    handleTargetTypeChange = (newType: TargetType) => {
        makeTargetTypeHandler<TargetType>(
            this.props.form,
            () => ({ targetType: this.state.targetType, savedTargets: this.state.savedTargets }),
            updater => this.setState(updater)
        )(newType)
    }

    handleSave = () => {
        if (this.state.saving || this.saveLock) return
        this.saveLock = true
        this.setState({ saving: true })

        const finish = () => {
            this.saveLock = false
            this.setState({ saving: false })
        }

        this.props.form.validateFields((err: any, values: any) => {
            if (err) {
                finish()
                return
            }
            const allValues = Object.assign({}, this.state.savedValues, values)
            const missingRequired = REQUIRED_CREATE_FIELDS.some(field => {
                const value = allValues[field]
                return value === undefined || value === null || (typeof value === 'string' ? value.trim() === '' : value === '')
            })
            if (missingRequired) {
                finish()
                return
            }

            const {
                hasWhereToUse,
                hasTermsLink,
                hasActionValue,
                hasExpiration,
                targetType,
                isOther,
                sendNotification
            } = this.state

            let target_value = null
            if (isOther) {
                const chips = parseTargets(allValues.targets || this.state.targetsInitial || '')
                target_value = chips.length > 0 ? chips : null
            }

            const body = {
                short_title: allValues.short_title,
                short_description: allValues.short_description,
                title: allValues.title,
                sub_title: allValues.sub_title,
                description: allValues.description,
                where_to_use: hasWhereToUse ? allValues.where_to_use || null : null,
                terms: allValues.terms,
                terms_link: hasTermsLink ? allValues.terms_link || null : null,
                action_type: allValues.action_type,
                action_value: hasActionValue ? allValues.action_value || null : null,
                benefit_type: allValues.benefit_type,
                target_type: isOther ? targetType : 'all',
                target_value,
                is_active: allValues.is_active ? 1 : 0,
                sort_order: allValues.sort_order || 0,
                expiration_date:
                    hasExpiration && allValues.expiration_date ? allValues.expiration_date.format('YYYY-MM-DD') : null
            }

            const benefitData = { title: allValues.title, description: allValues.short_description || '' }
            // Page always owns create/update — modal only emits
            ;(async () => {
                try {
                    const saved = await this.props.onSaved(sendNotification, benefitData, body)
                    if (saved === false) return
                } finally {
                    finish()
                }
            })()
        })
    }

    renderTargetChips() {
        const { targetType, isOther, targetsInitial } = this.state
        if (!isOther) return null
        return <TargetChipField form={this.props.form} targetType={targetType} targetsInitial={targetsInitial} className="mediwho-benefit-target-chip-input" />
    }

    renderStep0() {
        const { form: { getFieldDecorator }, benefit } = this.props
        const { hasWhereToUse } = this.state
        const getSavedFieldValue = this.wizard.getSavedFieldValue
        return (
            <div>
                {/* Card 1: short title + short description */}
                <div className="mediwho-card">
                    <SectionTitle label="כרטיס קצר" color="#1890ff" />
                    <Form.Item label="כותרת קצרה" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('short_title', {
                            initialValue: getSavedFieldValue('short_title', benefit ? benefit.short_title : ''),
                            rules: [{ required: true, message: 'שדה חובה' }]
                        })(<Input placeholder="כותרת קצרה" className="mediwho-benefit-short-title-input" />)}
                    </Form.Item>
                    <Form.Item label="תיאור קצר" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('short_description', {
                            initialValue: getSavedFieldValue('short_description', benefit ? benefit.short_description : ''),
                            rules: [{ required: true, message: 'שדה חובה' }]
                        })(<Input.TextArea rows={2} placeholder="תיאור קצר" className="mediwho-benefit-short-description-input" />)}
                    </Form.Item>
                </div>

                {/* Card 2: main title, subtitle, full description */}
                <div className="mediwho-card">
                    <SectionTitle label="כרטיס מלא" color="#52c41a" />
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Form.Item label="כותרת ראשית" style={{ flex: 1, marginBottom: 12 }}>
                            {getFieldDecorator('title', {
                                initialValue: getSavedFieldValue('title', benefit ? benefit.title : ''),
                                rules: [{ required: true, message: 'שדה חובה' }]
                            })(<Input placeholder="כותרת ראשית" className="mediwho-benefit-title-input" />)}
                        </Form.Item>
                        <Form.Item label="כותרת משנה" style={{ flex: 1, marginBottom: 12 }}>
                            {getFieldDecorator('sub_title', {
                                initialValue: getSavedFieldValue('sub_title', benefit ? benefit.sub_title : ''),
                                rules: [{ required: true, message: 'שדה חובה' }]
                            })(<Input placeholder="כותרת משנה" className="mediwho-benefit-subtitle-input" />)}
                        </Form.Item>
                    </div>
                    <Form.Item label="תיאור מלא" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('description', {
                            initialValue: getSavedFieldValue('description', benefit ? benefit.description : ''),
                            rules: [{ required: true, message: 'שדה חובה' }]
                        })(<Input.TextArea rows={3} placeholder="תיאור מלא" className="mediwho-benefit-description-input" />)}
                    </Form.Item>
                    <div style={{ marginBottom: hasWhereToUse ? 4 : 12 }}>
                        <Checkbox
                            className="mediwho-benefit-where-to-use-checkbox"
                            checked={hasWhereToUse}
                            onChange={e => this.setState({ hasWhereToUse: e.target.checked })}
                        >
                            איפה ניתן להשתמש?
                        </Checkbox>
                    </div>
                    {hasWhereToUse && (
                        <Form.Item style={{ marginBottom: 12, marginTop: 6 }}>
                            {getFieldDecorator('where_to_use', {
                                initialValue: getSavedFieldValue('where_to_use', benefit ? benefit.where_to_use : '')
                            })(<Input.TextArea rows={2} placeholder="איפה להשתמש" className="mediwho-benefit-where-to-use-input" />)}
                        </Form.Item>
                    )}
                </div>
            </div>
        )
    }

    renderStep1() {
        const { form: { getFieldDecorator }, benefit } = this.props
        const { hasTermsLink, hasActionValue } = this.state
        const getSavedFieldValue = this.wizard.getSavedFieldValue
        return (
            <div>
                <div className="mediwho-card">
                    <SectionTitle label="סוג ופעולה" color="#722ed1" />
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Form.Item label="סוג הטבה" style={{ flex: 1, marginBottom: 12 }}>
                            {getFieldDecorator('benefit_type', {
                                initialValue: getSavedFieldValue('benefit_type', benefit ? benefit.benefit_type : undefined),
                                rules: [{ required: true, message: 'שדה חובה' }]
                            })(
                                <Select placeholder="בחר סוג הטבה" className="commission-select mediwho-benefit-type-select" dropdownClassName="commission-dropdown">
                                    {BENEFIT_TYPES.map(t => (
                                        <Select.Option key={t.value} value={t.value}>{t.icon} {t.label}</Select.Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>
                        <Form.Item label="סוג פעולה" style={{ flex: 1, marginBottom: 12 }}>
                            {getFieldDecorator('action_type', {
                                initialValue: this.props.isPartner
                                    ? 'link'
                                    : getSavedFieldValue('action_type', benefit ? benefit.action_type : 'link'),
                                rules: [{ required: true, message: 'שדה חובה' }]
                            })(
                                <Select
                                    className="commission-select mediwho-benefit-action-type-select"
                                    dropdownClassName="commission-dropdown"
                                    disabled={this.props.isPartner}
                                >
                                    {ACTION_TYPES.map(t => (
                                        <Select.Option key={t.value} value={t.value}>{t.icon} {t.label}</Select.Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>
                    </div>
                    <div style={{ marginBottom: hasActionValue ? 4 : 12 }}>
                        <Checkbox
                            className="mediwho-benefit-action-value-checkbox"
                            checked={hasActionValue}
                            disabled={this.props.isPartner}
                            onChange={e => this.setState({ hasActionValue: e.target.checked })}
                        >
                            {this.props.isPartner ? 'הוסף קישור' : 'הוסף קישור / קוד קופון / endpoint'}
                        </Checkbox>
                    </div>
                    {hasActionValue && (
                        <Form.Item style={{ marginBottom: 12, marginTop: 6 }}>
                            {getFieldDecorator('action_value', {
                                initialValue: getSavedFieldValue('action_value', benefit ? benefit.action_value : '')
                            })(<Input placeholder={this.props.isPartner ? 'קישור' : 'קישור / קוד קופון / endpoint'} className="mediwho-benefit-action-value-input" />)}
                        </Form.Item>
                    )}
                </div>

                <div className="mediwho-card">
                    <SectionTitle label="תנאים" color="#fa8c16" />
                    <Form.Item label="תנאי ההטבה" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('terms', {
                            initialValue: getSavedFieldValue('terms', benefit ? benefit.terms : ''),
                            rules: [{ required: true, message: 'שדה חובה' }]
                        })(<Input.TextArea placeholder="תנאי ההטבה" rows={4} className="mediwho-benefit-terms-input" />)}
                    </Form.Item>
                    <div style={{ marginBottom: hasTermsLink ? 4 : 12 }}>
                        <Checkbox
                            className="mediwho-benefit-terms-link-checkbox"
                            checked={hasTermsLink}
                            onChange={e => this.setState({ hasTermsLink: e.target.checked })}
                        >
                            הוסף: קישור לתנאי שימוש
                        </Checkbox>
                    </div>
                    {hasTermsLink && (
                        <Form.Item style={{ marginBottom: 12, marginTop: 6 }}>
                            {getFieldDecorator('terms_link', {
                                initialValue: getSavedFieldValue('terms_link', benefit ? benefit.terms_link : '')
                            })(<Input placeholder="קישור לתנאי שימוש" className="mediwho-benefit-terms-link-input" />)}
                        </Form.Item>
                    )}
                </div>
            </div>
        )
    }

    renderStep2() {
        const { form: { getFieldDecorator }, benefit } = this.props
        const { hasExpiration, hasSortOrder, targetType, isOther, sendNotification, isActive } = this.state
        const getSavedFieldValue = this.wizard.getSavedFieldValue
        return (
            <div>
                {!this.props.isPartner && (
                <div className="mediwho-card">
                    <SectionTitle label="קהל יעד" color="#13c2c2" />
                    <Form.Item style={{ marginBottom: isOther ? 8 : 12 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: isOther ? 8 : 0 }}>
                            <CheckableTag
                                className="mediwho-benefit-target-all-tag"
                                checked={!isOther}
                                onChange={checked => {
                                    if (checked) this.setState({ isOther: false })
                                }}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 999,
                                    border: '1px solid #d9d9d9',
                                    marginRight: 0,
                                    fontWeight: 500
                                }}
                            >
                                כל המשתמשים
                            </CheckableTag>
                            <CheckableTag
                                className="mediwho-benefit-target-specific-tag"
                                checked={isOther}
                                onChange={checked => {
                                    if (checked) this.setState({ isOther: true })
                                }}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 999,
                                    border: '1px solid #d9d9d9',
                                    marginRight: 0,
                                    fontWeight: 500
                                }}
                            >
                                ספציפי
                            </CheckableTag>
                        </div>
                        {isOther && (
                            <Select
                                value={targetType}
                                onChange={(val: TargetType) => this.handleTargetTypeChange(val)}
                                style={{ width: '100%' }}
                                className="commission-select mediwho-benefit-target-type-select"
                                dropdownClassName="commission-dropdown"
                            >
                                {TARGET_OPTIONS.map(o => (
                                    <Select.Option key={o.value} value={o.value}>
                                        {o.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                    {this.renderTargetChips()}
                </div>
                )}

                <div className="mediwho-card">
                    <SectionTitle label="זמינות ומיון" color="#eb2f96" />
                    <div style={{ marginBottom: hasExpiration ? 4 : 12 }}>
                        <Checkbox
                            className="mediwho-benefit-expiration-checkbox"
                            checked={hasExpiration}
                            onChange={e => this.setState({ hasExpiration: e.target.checked })}
                        >
                            תאריך תפוגה
                        </Checkbox>
                    </div>
                    {hasExpiration && (
                        <Form.Item style={{ marginBottom: 12, marginTop: 6 }}>
                            {getFieldDecorator('expiration_date', {
                                initialValue: getSavedFieldValue(
                                    'expiration_date',
                                    benefit && benefit.expiration_date ? moment(benefit.expiration_date) : undefined
                                )
                            })(
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="בחר תאריך תפוגה"
                                    className="mediwho-benefit-expiration-date-input"
                                    disabledDate={d => d && d.isBefore(moment(), 'day')}
                                />
                            )}
                        </Form.Item>
                    )}

                    <div style={{ marginBottom: hasSortOrder ? 4 : 12 }}>
                        <Checkbox
                            className="mediwho-benefit-sort-order-checkbox"
                            checked={hasSortOrder}
                            onChange={e => this.setState({ hasSortOrder: e.target.checked })}
                        >
                            סדר מיון
                        </Checkbox>
                    </div>
                    {hasSortOrder && (
                        <Form.Item style={{ marginBottom: 12, marginTop: 6 }}>
                            {getFieldDecorator('sort_order', {
                                initialValue: getSavedFieldValue(
                                    'sort_order',
                                    benefit && benefit.sort_order ? benefit.sort_order : 1
                                )
                            })(
                                <Select style={{ width: '100%' }} className="commission-select mediwho-benefit-sort-order-select" dropdownClassName="commission-dropdown">
                                    {Array.from({ length: (() => {
                                        const isNew = !this.props.benefit
                                        // sort_order is assigned globally across all benefits (see saveOrder),
                                        // so the list must span all rows, not just the active/inactive group
                                        const total = (this.props.activeCount || 0) + (this.props.inactiveCount || 0)
                                        return (isNew ? total + 1 : total) || 1
                                    })() }, (_, i) => i + 1).map(n => {
                                        const taken = (this.props.takenByOthers || []).indexOf(n) !== -1
                                        return (
                                            <Select.Option key={n} value={n} disabled={taken}>
                                                {taken ? n + ' (תפוס)' : n}
                                            </Select.Option>
                                        )
                                    })}
                                </Select>
                            )}
                        </Form.Item>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <span>פעיל</span>
                        <Form.Item style={{ marginBottom: 0, paddingBottom: 0 }}>
                            {getFieldDecorator('is_active', {
                                initialValue: getSavedFieldValue('is_active', benefit ? !!benefit.is_active : true),
                                valuePropName: 'checked'
                            })(<Switch className="mediwho-benefit-active-switch" onChange={v => this.setState({ isActive: v })} />)}
                        </Form.Item>
                    </div>
                </div>

                {!this.props.isPartner && (
                    <SendNotificationCheckbox
                        checked={sendNotification}
                        onChange={v => this.setState({ sendNotification: v })}
                    />
                )}
            </div>
        )
    }

    render() {
        const { visible, onClose, benefit } = this.props
        const { saving, step, isOther } = this.state
        const title = benefit ? 'עריכת הטבה' : 'הטבה חדשה'
        const { handleNext, handleBack } = this.wizard
        const targetsValue = this.props.form.getFieldValue('targets') || this.state.targetsInitial || ''
        // isOther is always false for a partner (the target-audience card is hidden), so this
        // is already false for them - stated explicitly so a future change to isOther can't lock them out
        const publishDisabled = !this.props.isPartner && step === 2 && isOther && parseTargets(targetsValue).length === 0

        const stepContent = [this.renderStep0(), this.renderStep1(), this.renderStep2()]

        const footer = renderWizardFooter({
            step,
            lastStep: 2,
            saving,
            form: this.props.form,
            requiredFieldsByStep: STEP_FIELDS,
            extraDisabled: publishDisabled,
            onNext: handleNext,
            onBack: handleBack,
            onSave: this.handleSave,
            onCancel: onClose,
        })

        return (
            <Modal
                visible={visible}
                title={title}
                onCancel={onClose}
                width={720}
                centered
                wrapClassName="mediwho-app-modal-wrap mediwho-benefit-modal-wrap"
                bodyStyle={{ padding: '24px 24px 8px' }}
                footer={footer}
            >
                <style>{COMMISSION_SELECT_STYLES}</style>
                <style>{`
                    .mediwho-benefit-modal-wrap .anticon.anticon-close-circle.ant-calendar-picker-clear,
                    .mediwho-benefit-expiration-date-input .anticon.anticon-close-circle.ant-calendar-picker-clear {
                        position: absolute;
                        top: 15px;
                        right: 96%;
                    }
                `}</style>
                <Form layout="vertical" className="mediwho-app-modal">
                    <StepBar current={step} steps={STEPS} />
                    {stepContent[step]}
                </Form>
            </Modal>
        )
    }
}

export default Form.create<Props>()(BenefitModal)
